import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, User, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { adminLogin } from '@/lib/api';
import { RTBLogo } from '@/components/RTBLogo';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await adminLogin({ username, password });
      localStorage.setItem('lbu_admin_token', data.token);
      toast.success('Login successful');
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-4" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-12">
          <RTBLogo dark className="h-12" />
        </div>

        <div className="glass-panel p-8">
          <h1 className="text-2xl font-oswald font-bold text-center mb-2">Admin Login</h1>
          <p className="text-white/60 text-center text-sm mb-8">
            Access the lead management dashboard
          </p>

          {error && (
            <div className="mb-6 p-4 bg-signal-red/10 border border-signal-red/30 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-signal-red" />
              <span className="text-signal-red text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="username" className="text-white/80 mb-2 block">Username</Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="input-dark pl-12"
                  placeholder="Enter username"
                  required
                  data-testid="admin-username-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-white/80 mb-2 block">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-dark pl-12"
                  placeholder="Enter password"
                  required
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="btn-primary w-full"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <p className="text-white/40 text-xs text-center mt-6">
            Default credentials: admin / admin123
          </p>
        </div>

        <button 
          onClick={() => navigate('/')}
          className="block mx-auto mt-8 text-white/40 hover:text-white text-sm transition-colors"
        >
          ← Back to main site
        </button>
      </motion.div>
    </div>
  );
}
